import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  IconButton,
  Typography,
  Checkbox,
  Modal,
  Slide,
  Box,
  Alert,
} from "@mui/material";
import { ReactComponent as CopyFromDoctorIcon } from "../../../../assets/icons/MedintCopyFromDoctorIcon.svg";
import { ReactComponent as ModifyIcon } from "../../../../assets/icons/MedintModifyIcon.svg";
import { ReactComponent as DeleteIcon } from "../../../../assets/icons/MedintDeleteIcon.svg";
import { useContext, useMemo, useState } from "react";
import { MedintDataContext } from "../../Context/MedintContextWrapper";
import { medPrest, panodi } from "../../../../networking";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

//plugin per il parsing delle date
dayjs.extend(customParseFormat);

export interface MedintTableProps {
  headers: string[];
  data: Array<{ [key: string]: any }>;

  fetchDataMedico?: () => Promise<void>;

  selectedAgenda?: number;
  setSelectedAgenda?: (number: number) => void;

  selectedErogate?: Array<{ [key: string]: any }>;
  setSelectedErogate?: (value: Array<{ [key: string]: any }>) => void;

  selectedAbbinate?: Array<{ [key: string]: any }>;
  setSelectedAbbinate?: (value: Array<{ [key: string]: any }>) => void;

  dataPrestazioniAbbinate?: any;
  setPrestazioniAbbinate?: (value: any) => void;

  showActionButton?: boolean; // Se true, mostra il bottone nella prima colonna della head
  actionButtonLabel?: string; // Label per il bottone

  onEditClick?: (row: Record<string, any>, rowIndex: number) => void;
  onCopyClick?: (row: Record<string, any>, rowIndex: number) => void;
  onDeleteClick?: (row: Record<string, any>, rowIndex: number) => void;

  // opzionale: se vuoi un bottone “azione principale” (non lo usi ora)
  onActionClick?: (row: Record<string, any>, rowIndex: number) => void;
}

function AllertConfirmDelete(props: {
  message?: string;
  severity?: any;
  showAllertDelete: boolean;
  setAllertDelete: (value: boolean) => void;
  handleDeleteConfirmation: () => Promise<void>;
}) {
  return (
    <Modal open={props.showAllertDelete}>
      <Slide in={props.showAllertDelete} direction="down" timeout={800}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "50vh",
            "& .MuiPaper-root": {
              paddingRight: 5,
              paddingLeft: 5,
            },
          }}
        >
          <Alert
            sx={{
              maxWidth: "150vh",
              width: "90%",
              alignContent: "center",
              alignItems: "center",
              justifyContent: "center",
              justifyItems: "center",
              transform: "translate3d(20%, 100%, 0%)",
              flexDirection: "row",
              "& .MuiAlert-icon": {
                fontSize: 30,
              },
            }}
            severity={props.severity || "warning"}
            action={
              props.severity !== "success" && (
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Button
                    variant="containedError"
                    size="small"
                    onClick={() => {
                      props.setAllertDelete(false);
                    }}
                  >
                    Uscita
                  </Button>
                  <Button
                    variant="containedCustom"
                    size="small"
                    onClick={() => {
                      props.handleDeleteConfirmation();
                    }}
                  >
                    Conferma
                  </Button>
                </Box>
              )
            }
          >
            <Typography
              component="div"
              marginTop={"20px"}
              marginLeft={"20px"}
              marginRight={"10px"}
              marginBottom={"20px"}
              fontSize={"20px"}
              fontStyle={"capitalize"}
            >
              {props.message ||
                `Procedere con la cancellazione dell'elemento selezionato?`}
            </Typography>
          </Alert>
        </Box>
      </Slide>
    </Modal>
  );
}

const MedintTable: React.FC<MedintTableProps> = ({
  headers,
  data,
  selectedAgenda,
  setSelectedAgenda,
  selectedErogate,
  setSelectedErogate,
  selectedAbbinate,
  setSelectedAbbinate,
  fetchDataMedico,
  dataPrestazioniAbbinate,
  setPrestazioniAbbinate,
  showActionButton = false,
  actionButtonLabel = "Aggiungi",
  onActionClick,
  onEditClick,
  onCopyClick,
  onDeleteClick,
}) => {
  const context = useContext(MedintDataContext);

  const [showAllert, setShowAllert] = useState<boolean>(false);
  const [severity, setSeverity] = useState<string>("");
  const [message, setMessage] = useState<string>("");

  const [rowToDelete, setRowToDelete] = useState<any>(null);

  const handleRowClick = (data: any) => {
    const mappedDataAbbinate = data.prestazioniabbinate || [];
    context?.setPrestazioniAbbinate(mappedDataAbbinate);
  };

  const sendDataToDelete = async (): Promise<void> => {
    if (rowToDelete?.IdMedPrest) {
      try {
        const response = await medPrest({
          idMedint: context?.doctorAgenda.medico[0].IDMEDICO,
          idMedprest: rowToDelete?.IdMedPrest,
          crud: "D",
          prestazioni: [
            {
              idSpecia: "",
              codiceSpecia: "",
              idAnapre: "",
              codiceAnapre: "",
              idTpgpre: "",
              codiceTpgpre: "",
              durataMin: "",
              attesaMin: "",
              nMaxPrest: "",
              minutiInizio: "",
              dataFineValidita: "",
              flagAccettazione: "",
              nota1: "",
              nota2: "",
              idPgmedt: "",
              programma: "",
              etaMin: "",
              etaMax: "",
              genere: "",
              flagAgendaParticolare: "",
              codiceParticolarita: "",
              flagRaggruppabile: "",
              codiceRaggruppamento: "",
              ricercaRagggp: "",
              flagEscludiGP: "",
              tempoPreparazione: "",
            },
          ],
        });

        setMessage(response.data.messaggio);

        console.log("Risposta della send", response);

        if (response.data.esito === "OK") {
          setSeverity("success");
        } else if (response.data.esito === "KO") {
          setSeverity("warning");
        } else {
          setSeverity("error");
          setMessage(
            "Cancellazione record dal database fallita, contattare l'amministratore",
          );
        }
      } catch (error) {
        console.warn("Si è presentato un errore durante l'eliminazione", error);
      } finally {
        if (fetchDataMedico) {
          await fetchDataMedico();
        }

        setTimeout(() => setShowAllert(false), 3000);
        setTimeout(() => setMessage(""), 4000);
        setTimeout(() => setSeverity("warning"), 4000);
      }
    } else if (rowToDelete?.idPanodi) {
      const rawDate = rowToDelete?.["Data Indisponibilità"];
      const parsedDate = dayjs(rawDate, "DD/MM/YYYY");

      if (!parsedDate.isValid()) {
        console.warn("Data non valida:", rawDate);
        setSeverity("error");
        setMessage("Data non valida per la cancellazione.");
        setShowAllert(true);
        return;
      }

      const formattedDate = parsedDate.format("YYYY-MM-DD");

      try {
        const idMedico = context?.doctorAgenda?.medico?.[0]?.IDMEDICO;
        const response = await panodi({
          crud: "D",
          idMedint: idMedico,
          idPanodi: rowToDelete?.IdPanodi,
          dataInizio: formattedDate,
          dataFine: formattedDate,
          giorniSelezionati: [true, true, true, true, true, true, false],
        });

        console.log("Risposta della send", response);

        const esito = response.data?.[0]?.esito;
        const messaggio = response.data?.[0]?.messaggio;

        if (esito === "OK") {
          setSeverity("success");
          setMessage(messaggio || "Indisponibilità salvata con successo");
        } else {
          console.warn("⚠️ Errore salvataggio:", messaggio);
          setSeverity("warning");
          setMessage(messaggio || "Errore nella registrazione");
        }

        setShowAllert(true);
      } catch (error) {
        setSeverity("error");
        setMessage("Errore durante il salvataggio. Contatta l'amministratore.");
        setShowAllert(true);
        console.error(error);
      } finally {
        if (fetchDataMedico) {
          await fetchDataMedico(); // <--- attendi la refetch
        }

        setTimeout(() => setShowAllert(false), 3000);
        setTimeout(() => setMessage(""), 4000);
        setTimeout(() => setSeverity("warning"), 4000);
      }
    } else {
      throw new Error("Nessun ID valido per l'eliminazione");
    }
  };

  const handleClickOnDelete = async (row: any) => {
    setRowToDelete(row);
    setShowAllert(true);
  };

  return (
    <>
      <>
        <AllertConfirmDelete
          showAllertDelete={showAllert}
          setAllertDelete={setShowAllert}
          handleDeleteConfirmation={sendDataToDelete}
          message={message}
          severity={severity}
        />
      </>
      <TableContainer
        sx={{
          boxSizing: "border-box",
          width: "auto",
          display: "flex",
          maxHeight: "350px",
          borderRadius: "8px",
          border: "1px solid rgba(204, 204, 204, 0.80)",
        }}
      >
        <Table
          size="small"
          stickyHeader
          sx={{ minWidth: 400, boxSizing: "border-box" }}
        >
          <TableHead sx={{ boxSizing: "border-box" }}>
            <TableRow
              sx={{
                backgroundColor: "#002F6C",
                height: "40px",
                boxSizing: "border-box",
              }}
            >
              {(selectedAbbinate || selectedErogate) && (
                <TableCell>{""}</TableCell>
              )}
              {headers.map((header, index) => (
                <TableCell
                  key={index}
                  sx={{ color: "white", fontWeight: "bold" }}
                >
                  {header}
                </TableCell>
              ))}

              {showActionButton && (
                <TableCell
                  sx={{
                    color: "white",
                    fontWeight: "bold",
                  }}
                >
                  Azioni
                </TableCell>
              )}
            </TableRow>
          </TableHead>

          <TableBody sx={{ boxSizing: "border-box" }}>
            {data.map((row, rowIndex) => (
              <TableRow
                key={rowIndex}
                hover={rowIndex !== selectedAgenda}
                onClick={() => {
                  console.log(row);
                  
                  // stato che gestisce la tabella di selezione di una agenda
                  if (setSelectedAgenda) setSelectedAgenda(rowIndex);

                  if (setPrestazioniAbbinate) handleRowClick(row);

                  if (rowIndex === selectedAgenda) {
                    if (setSelectedAgenda) {
                      setSelectedAgenda(-1);
                      context?.setPrestazioniAbbinate([]);
                    }
                  }

                  if (setSelectedErogate && selectedErogate) {
                    setSelectedErogate([...selectedErogate, row]);

                    if (selectedErogate?.includes(row)) {
                      setSelectedErogate(
                        selectedErogate.filter((index) => index !== row),
                      );
                    }
                  }

                  if (setSelectedAbbinate && selectedAbbinate) {
                    setSelectedAbbinate([...selectedAbbinate, row]);

                    if (selectedAbbinate?.includes(row)) {
                      setSelectedAbbinate(
                        selectedAbbinate.filter((index) => index !== row),
                      );
                    }
                  }
                }}
                sx={{
                  backgroundColor:
                    rowIndex === selectedAgenda ||
                    selectedErogate?.includes(row) ||
                    selectedAbbinate?.includes(row)
                      ? "#fff1dc"
                      : rowIndex % 2 === 0
                        ? "white"
                        : "#E6F0FA",
                  height: "35px",
                }}
              >
                {(selectedAbbinate || selectedErogate) && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      color="primary"
                      checked={Boolean(
                        selectedErogate?.includes(row) ||
                        selectedAbbinate?.includes(row),
                      )}
                      inputProps={{
                        "aria-labelledby": rowIndex.toString(),
                      }}
                    />
                  </TableCell>
                )}
                {headers.map((header, colIndex) => (
                  <TableCell key={colIndex}>
                    <Typography
                      fontSize={
                        row[header] !== undefined
                          ? row[header].length > 24
                            ? 11
                            : ""
                          : ""
                      }
                    >
                      {row[header] !== undefined ? row[header] : "—"}
                    </Typography>
                  </TableCell>
                ))}

                {showActionButton && (
                  <TableCell>
                    {onEditClick &&
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEditClick?.(row, rowIndex);
                      }}
                      sx={{
                        color: "#024E88",
                      }}
                    >
                      <ModifyIcon />
                    </IconButton>}

                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onCopyClick?.(row, rowIndex);
                      }}
                      sx={{
                        color: "#024E88",
                      }}
                    >
                      {" "}
                      <CopyFromDoctorIcon />
                    </IconButton>

                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClickOnDelete(row);
                      }}
                      sx={{
                        color: "#D32F2F",
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default MedintTable;
